import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, LogOut, Loader2, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import api, { errorMessage } from "@/lib/api";

const EMPTY_CREATE = { name: "", email: "", password: "" };

export default function CompanyAdmin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (e) {
      toast.error("Erro ao carregar utilizadores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", createForm);
      toast.success("Operador criado", { description: createForm.email });
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      fetchUsers();
    } catch (e) {
      toast.error("Erro ao criar operador", { description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name, email: u.email, active: u.active, password: "" });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: editForm.name, email: editForm.email, active: editForm.active };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editTarget.id}`, payload);
      toast.success("Operador atualizado");
      setEditTarget(null);
      fetchUsers();
    } catch (e) {
      toast.error("Erro ao atualizar operador", { description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 bg-card">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            title="Voltar ao visualizador"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 bg-foreground flex items-center justify-center">
            <Users className="w-4 h-4 text-background" />
          </div>
          <div className="leading-none">
            <h1 className="font-head text-sm font-bold tracking-tight">UTILIZADORES · {user?.company_name}</h1>
            <p className="text-[10px] text-muted-foreground mono mt-0.5">Gestão de operadores</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground mono hidden sm:inline">{user?.name}</span>
          <button
            onClick={logout}
            title="Terminar sessão"
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Operadores</h2>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-[4px]" data-testid="new-operator-btn">
                <Plus className="w-4 h-4" /> Novo operador
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Novo operador</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Nome</Label>
                  <Input required value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Email (login)</Label>
                  <Input required type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Password</Label>
                  <Input required type="password" minLength={8} value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="rounded-[4px]">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Criar operador
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline" /> A carregar...
                </TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Nenhum operador criado.</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">
                    {u.name} {u.role === "company_admin" && <Badge variant="outline" className="ml-2 align-middle">Chefe</Badge>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant={u.active ? "default" : "secondary"}>{u.active ? "Ativo" : "Inativo"}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {u.role === "operator" ? (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground/60">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar {editTarget?.name}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleEditSave} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Email (login)</Label>
                <Input required type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Nova password (opcional)</Label>
                <Input type="password" minLength={8} placeholder="Deixar em branco para manter" value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="user-active">Operador ativo</Label>
                <Switch id="user-active" checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="rounded-[4px]">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
